import React, { useState } from 'react'

import { Button, IconButton, Typography } from '@material-ui/core'
import KeyboardArrowLeftIcon from '@material-ui/icons/KeyboardArrowLeft'
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight'
import { DateTime } from 'luxon'

import { useWalkScheduleStyles } from './styles'
import { inclusiveDayCount } from './timelineRules'

interface Props {
    zone: string
    start: DateTime | null
    end: DateTime | null
    minDate: Date | null
    maxDate: Date | null
    boundsLabel: string | null
    error: string | null
    saving: boolean
    onChange: (start: DateTime | null, end: DateTime | null) => void
    onSave: () => void
    onCancel: (() => void) | null
}

type ActiveField = 'start' | 'end'

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

const dateKey = (date: DateTime) => date.toFormat('yyyy-LL-dd')

const jsDateKey = (date: Date | null) =>
    date ? DateTime.fromJSDate(date).toFormat('yyyy-LL-dd') : null

const monthCells = (month: DateTime) => {
    const first = month.startOf('month')
    const lead = first.weekday % 7
    const cells: Array<DateTime | null> = []
    for (let index = 0; index < lead; index += 1) cells.push(null)
    const days = first.daysInMonth ?? 0
    for (let day = 0; day < days; day += 1)
        cells.push(first.plus({ days: day }))
    while (cells.length % 7 !== 0) cells.push(null)
    return cells
}

export const WalkTimelinePicker = (props: Props) => {
    const {
        zone,
        start,
        end,
        minDate,
        maxDate,
        boundsLabel,
        error,
        saving,
        onChange,
        onSave,
        onCancel,
    } = props
    const classes = useWalkScheduleStyles()
    const [activeField, setActiveField] = useState<ActiveField>(
        start && !end ? 'end' : 'start',
    )
    const [hoverKey, setHoverKey] = useState<string | null>(null)
    const [visibleMonth, setVisibleMonth] = useState(() => {
        const anchor = start
            ? start
            : minDate
            ? DateTime.fromJSDate(minDate)
            : DateTime.now().setZone(zone)
        return anchor.startOf('month')
    })

    const minKey = jsDateKey(minDate)
    const maxKey = jsDateKey(maxDate)
    const startKey = start ? dateKey(start) : null
    const endKey = end ? dateKey(end) : null
    const inBounds = (key: string) =>
        (!minKey || key >= minKey) && (!maxKey || key <= maxKey)
    const canSelect = (key: string) => {
        if (!inBounds(key)) return false
        if (activeField === 'end' && startKey && key < startKey) return false
        return true
    }
    const previewEndKey =
        activeField === 'end' &&
        startKey &&
        hoverKey &&
        hoverKey >= startKey &&
        inBounds(hoverKey)
            ? hoverKey
            : endKey
    const rangeLow =
        startKey && previewEndKey
            ? startKey < previewEndKey
                ? startKey
                : previewEndKey
            : null
    const rangeHigh =
        startKey && previewEndKey
            ? startKey > previewEndKey
                ? startKey
                : previewEndKey
            : null

    const leftMonth = visibleMonth.startOf('month')
    const rightMonth = leftMonth.plus({ months: 1 })
    const minMonth = minDate
        ? DateTime.fromJSDate(minDate).startOf('month')
        : null
    const maxMonth = maxDate
        ? DateTime.fromJSDate(maxDate).startOf('month')
        : null
    const monthIndex = (month: DateTime) => month.year * 12 + month.month
    const canPrev = !minMonth || monthIndex(leftMonth) > monthIndex(minMonth)
    const canNext = !maxMonth || monthIndex(rightMonth) < monthIndex(maxMonth)

    const selectDay = (day: DateTime) => {
        const next = day.setZone(zone, { keepLocalTime: true }).startOf('day')
        if (!canSelect(dateKey(next))) return
        if (activeField === 'start') {
            const clearsEnd = Boolean(end && next > end.startOf('day'))
            onChange(next, clearsEnd ? null : end)
            setActiveField('end')
            return
        }
        if (!start || next < start.startOf('day')) {
            onChange(next, null)
            setActiveField('end')
            return
        }
        onChange(start, next)
    }

    const dayCount = start && end ? inclusiveDayCount(start, end) : null
    const hint =
        activeField === 'start'
            ? 'Select the start date.'
            : end
            ? 'Select a new end date, or save.'
            : 'Select the end date.'

    const renderMonth = (month: DateTime) => (
        <div className={classes.monthBlock}>
            <Typography className={classes.monthTitle}>
                {month.toFormat('LLLL yyyy')}
            </Typography>
            <div className={classes.weekdayRow}>
                {WEEKDAYS.map((label) => (
                    <div key={label} className={classes.weekday}>
                        {label}
                    </div>
                ))}
            </div>
            <div
                className={classes.monthGrid}
                onMouseLeave={() => setHoverKey(null)}
            >
                {monthCells(month).map((day, index) => {
                    if (!day) {
                        return (
                            <div
                                key={`empty-${month.month}-${index}`}
                                className={classes.dayCell}
                            />
                        )
                    }
                    const key = dateKey(day)
                    const edge = key === startKey || key === endKey
                    const inRange = Boolean(
                        rangeLow &&
                            rangeHigh &&
                            inBounds(key) &&
                            key >= rangeLow &&
                            key <= rangeHigh,
                    )
                    const selectable = canSelect(key)
                    return (
                        <div
                            key={key}
                            className={`${classes.dayCell} ${
                                inRange && !edge ? classes.rangeDay : ''
                            }`}
                            onMouseEnter={() =>
                                setHoverKey(selectable ? key : null)
                            }
                        >
                            <button
                                type="button"
                                className={`${classes.dayButton} ${
                                    edge ? classes.rangeEdge : ''
                                }`}
                                disabled={!selectable}
                                onClick={() => selectDay(day)}
                            >
                                {day.day}
                            </button>
                        </div>
                    )
                })}
            </div>
        </div>
    )

    const fieldClass = (field: ActiveField) =>
        `${classes.fieldButton} ${
            activeField === field ? classes.fieldButtonActive : ''
        }`

    return (
        <div className={classes.pickerWrap}>
            <div className={classes.pickerCard}>
                <Typography className={classes.pickerTitle}>
                    Project timeline
                </Typography>
                {boundsLabel && (
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        className={classes.pickerHint}
                    >
                        {boundsLabel}
                    </Typography>
                )}
                <Typography
                    variant="body2"
                    color="textSecondary"
                    className={classes.pickerHint}
                >
                    {hint}
                </Typography>
                <div className={classes.fieldRow}>
                    <button
                        type="button"
                        className={fieldClass('start')}
                        onClick={() => setActiveField('start')}
                    >
                        <span className={classes.fieldLabel}>Start</span>
                        <span className={classes.fieldValue}>
                            {start ? start.toFormat('LLL d, yyyy') : 'Select'}
                        </span>
                    </button>
                    <button
                        type="button"
                        className={fieldClass('end')}
                        disabled={!start}
                        onClick={() => setActiveField('end')}
                    >
                        <span className={classes.fieldLabel}>End</span>
                        <span className={classes.fieldValue}>
                            {end ? end.toFormat('LLL d, yyyy') : 'Select'}
                        </span>
                    </button>
                </div>
                <div className={classes.calendarRow}>
                    <IconButton
                        aria-label="Previous months"
                        disabled={!canPrev}
                        onClick={() =>
                            setVisibleMonth((month) =>
                                month.minus({ months: 1 }).startOf('month'),
                            )
                        }
                    >
                        <KeyboardArrowLeftIcon />
                    </IconButton>
                    {renderMonth(leftMonth)}
                    {renderMonth(rightMonth)}
                    <IconButton
                        aria-label="Next months"
                        disabled={!canNext}
                        onClick={() =>
                            setVisibleMonth((month) =>
                                month.plus({ months: 1 }).startOf('month'),
                            )
                        }
                    >
                        <KeyboardArrowRightIcon />
                    </IconButton>
                </div>
                <Typography className={classes.dayCount}>
                    {dayCount == null
                        ? ' '
                        : `${dayCount} ${dayCount === 1 ? 'day' : 'days'}`}
                </Typography>
                {error && (
                    <Typography variant="body2" className={classes.pickerError}>
                        {error}
                    </Typography>
                )}
                <div className={classes.pickerActions}>
                    {onCancel && (
                        <Button onClick={onCancel} disabled={saving}>
                            Cancel
                        </Button>
                    )}
                    <Button
                        color="primary"
                        variant="contained"
                        disabled={!start || !end || Boolean(error) || saving}
                        onClick={onSave}
                    >
                        Save
                    </Button>
                </div>
            </div>
        </div>
    )
}
